import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PROSPECTS_KEY,
  createProspect,
  importProspects,
  observeProspect,
  observeProspects,
  recordCall,
  rouvrirProspect,
  updateProspectFields,
  updateProspectStatus
} from '../services/prospectService';

/**
 * Accès aux prospects — copié sur `useCreationRequests.js` (mêmes patterns, aucune invention).
 *
 * Deux points non négociables :
 *  - **`onSnapshot` + filtres dans `select`** : jamais une requête Firestore différente par
 *    filtre. C'est ce qui limite la surface aux 2 index du §4.1.
 *  - **`invalidateQueries` sur la clé de liste** après chaque mutation : sans ça, la file d'appel
 *    garde un prospect qui vient de passer `perdu` jusqu'au rechargement de la page.
 */

const DETAILS_KEY = 'prospectDetails';

/** Filtres appliqués côté client, sur la liste déjà chargée. */
export const appliquerFiltres = (prospects = [], filtres = {}) => {
  const {
    status = '',
    departement = '',
    metier = '',
    nonRevendiqueesSeulement = false,
    scoreMin = 0,
    recherche = ''
  } = filtres || {};

  const terme = String(recherche || '').trim().toLowerCase();

  return (Array.isArray(prospects) ? prospects : []).filter((prospect) => {
    if (status && prospect.status !== status) return false;
    if (departement && prospect.departmentCode !== departement) return false;
    if (metier && prospect.targetTrade !== metier) return false;
    if (nonRevendiqueesSeulement && prospect.google?.claimed !== false) return false;
    if (scoreMin && (Number(prospect.score) || 0) < Number(scoreMin)) return false;
    if (terme) {
      const champs = [prospect.name, prospect.address?.city, prospect.primaryCategory, prospect.phone];
      const correspond = champs.some((champ) => String(champ || '').toLowerCase().includes(terme));
      if (!correspond) return false;
    }
    return true;
  });
};

/**
 * Liste temps réel des prospects.
 * @param {object} filtres statut / département / métier / non revendiquées / score min / recherche
 */
export const useProspects = (filtres = {}) => {
  const queryClient = useQueryClient();
  const [pret, setPret] = useState(false);
  const [erreurFlux, setErreurFlux] = useState(null);

  useEffect(() => {
    const unsubscribe = observeProspects(
      (liste) => {
        queryClient.setQueryData([PROSPECTS_KEY], liste);
        setErreurFlux(null);
        setPret(true);
      },
      (erreur) => {
        setErreurFlux(erreur);
        setPret(true);
      }
    );

    return () => unsubscribe();
  }, [queryClient]);

  const { data, isError, error, refetch } = useQuery({
    queryKey: [PROSPECTS_KEY],
    // Le flux `onSnapshot` alimente le cache : aucune lecture Firestore supplémentaire.
    queryFn: () => Promise.resolve(queryClient.getQueryData([PROSPECTS_KEY]) || []),
    staleTime: Infinity,
    select: (liste) => appliquerFiltres(liste, filtres)
  });

  // Même clé, pas de `select` : la liste complète alimente les compteurs d'onglets
  const { data: tous } = useQuery({
    queryKey: [PROSPECTS_KEY],
    queryFn: () => Promise.resolve(queryClient.getQueryData([PROSPECTS_KEY]) || []),
    staleTime: Infinity
  });

  const prospects = data || [];
  const erreur = erreurFlux || error;

  return {
    prospects,
    tous: tous || [],
    isLoading: !pret,
    isError: Boolean(erreur) || isError,
    error: erreur,
    // `permission-denied` : LA cause n°1 est la règle Firestore manquante (§4.1)
    permissionDenied: erreur?.code === 'permission-denied',
    refetch
  };
};

/**
 * Détail temps réel d'une fiche + toutes ses mutations.
 * @param {string|null} placeId
 */
export const useProspectDetail = (placeId) => {
  const queryClient = useQueryClient();
  const [prospect, setProspect] = useState(null);
  const [pret, setPret] = useState(false);
  const [erreurFlux, setErreurFlux] = useState(null);

  useEffect(() => {
    if (!placeId) {
      setProspect(null);
      setPret(true);
      return undefined;
    }

    setPret(false);
    const unsubscribe = observeProspect(
      placeId,
      (document) => {
        setProspect(document);
        setErreurFlux(null);
        setPret(true);
      },
      (erreur) => {
        setErreurFlux(erreur);
        setPret(true);
      }
    );

    return () => unsubscribe();
  }, [placeId]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] });
    queryClient.invalidateQueries({ queryKey: [DETAILS_KEY, placeId] });
  }, [queryClient, placeId]);

  const enregistrerAppelMutation = useMutation({
    mutationFn: ({ outcome, options }) => recordCall(placeId, outcome, { ...options, prospect }),
    onSuccess: invalidate
  });

  const changerStatutMutation = useMutation({
    mutationFn: ({ status, uid }) => updateProspectStatus(placeId, status, { uid }),
    onSuccess: invalidate
  });

  const rouvrirMutation = useMutation({
    mutationFn: ({ uid }) => rouvrirProspect(placeId, { uid }),
    onSuccess: invalidate
  });

  const majChampsMutation = useMutation({
    mutationFn: ({ patch, uid }) => updateProspectFields(placeId, patch, { uid }),
    onSuccess: invalidate
  });

  const erreur =
    erreurFlux ||
    enregistrerAppelMutation.error ||
    majChampsMutation.error ||
    changerStatutMutation.error;

  return {
    prospect,
    isLoading: !pret,
    isError: Boolean(erreurFlux),
    error: erreur,
    permissionDenied: erreur?.code === 'permission-denied',
    enregistrerAppel: enregistrerAppelMutation.mutateAsync,
    enregistrerAppelEnCours: enregistrerAppelMutation.isPending,
    changerStatut: changerStatutMutation.mutateAsync,
    rouvrir: rouvrirMutation.mutateAsync,
    mettreAJourChamps: majChampsMutation.mutateAsync,
    majChampsEnCours: majChampsMutation.isPending
  };
};

/**
 * Import d'un fichier Apify : `onSuccess` invalide la liste pour que la file d'appel se remplisse
 * sans rechargement.
 */
export const useImportProspects = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ documents, uid, resume }) => importProspects(documents, { uid, resume }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] });
    }
  });

  return {
    importer: mutation.mutateAsync,
    enCours: mutation.isPending,
    resultat: mutation.data,
    erreur: mutation.error,
    reinitialiser: mutation.reset
  };
};

/**
 * Création d'une fiche saisie à la main (§5.2 bis).
 *
 * La fiche apparaît dans la file sans rechargement : `invalidateQueries` sur la clé de liste,
 * exactement comme l'import.
 */
export const useCreateProspect = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ document, uid }) => createProspect(document, { uid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] });
    }
  });

  return {
    creer: mutation.mutateAsync,
    enCours: mutation.isPending,
    erreur: mutation.error,
    reinitialiser: mutation.reset
  };
};
